import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import Pair from 'app/common/pair'
import { OutputType } from 'app/constants/outputType'
import { downloadFile } from './BaseService'
import { axiosInstance } from '@/lib/axios'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

/** Odpowiedź /printout/capabilities — jeden wpis na typ raportu. */
export type ReportCapabilityDto = {
  reportType: number
  hasPdf: boolean
  hasCsv: boolean
  hasXlsx: boolean
}

const usePrintOutService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { activeLanguage } = useLocalize()
  const getOutputType = (outputType: OutputType) => {
    return outputType === OutputType.Pdf ? 'application/pdf' : 'application/csv'
  }

  const getOutputFile = (id: string) =>
    downloadFile(
      `/config-api/importTask/downloadOutputFile?id=${id}`,
      {},
      true,
      'application/octet-stream',
    )

  const getPdf = ({
    companyId,
    reportType,
    drivers,
    vehicles,
    startTime,
    endTime,
    outputType,
    id,
    data = {},
  }: {
    companyId: string
    reportType: number
    drivers: number[] | null
    vehicles: number[] | null
    startTime: string
    endTime: string
    outputType: OutputType
    id: string
    data: any
  }) => {
    const payload = {
      id,
      drivers: drivers ? drivers.map((d) => +d) : null,
      vehicles: vehicles ? vehicles.map((d) => +d) : null,
      reportType,
      startTime: startTime,
      endTime: endTime,
      outputType,
      data,
      language: activeLanguage?.code,
    }
    return axiosInstance.post(`print-api/${companyId}/printout/print`, payload)
  }

  const getPdfFromDates = (
    companyId,
    reportType,
    drivers,
    vehicles,
    dates,
    outputType: OutputType,
    id,
    data: any = {},
  ) => {
    const payload = {
      id,
      drivers: drivers ? drivers.map((d) => +d) : null,
      vehicles: vehicles ? vehicles.map((d) => +d) : null,
      reportType,
      dates,
      outputType,
      data,
      language: activeLanguage?.code,
    }
    return axiosInstance.post(`print-api/${companyId}/printout/print`, payload)
  }

  const getActiveDriversReport = (
    companies,
    groups,
    months,
    year,
    outputType: OutputType,
    showDetails,
    showActiveness,
    showTimesheet,
    showBusinessTrip,
    showPrintout,
    showBasic,
    showPRO,
    showVPN,
    showUpdater,
    showCurrentlyActive,
  ) =>
    downloadFile(
      `config-api/printout/activeDrivers`,
      {
        companies,
        groups,
        months,
        year,
        csv: outputType == OutputType.Csv,
        showDetails,
        showActiveness,
        showTimesheet,
        showBusinessTrip,
        showPrintout,
        showBasic,
        showPRO,
        showVPN,
        showUpdater,
        showCurrentlyActive,
      },
      true,
      getOutputType(outputType),
    )

  const getAuditReport = (
    companies,
    groups,
    user,
    startDate,
    endDate,
    events,
    outputType: OutputType,
    sortBy: number,
  ) =>
    downloadFile(
      `config-api/printout/audit`,
      {
        companies,
        groups,
        user,
        startDate,
        endDate,
        events,
        csv: outputType == OutputType.Csv,
        sortBy,
      },
      true,
      getOutputType(outputType),
    )

  const getSynchronizationReport = (
    companies,
    user,
    startDate,
    endDate,
    outputType: OutputType,
    sortBy: number,
    onlyUpdater: boolean,
  ) =>
    downloadFile(
      `config-api/printout/synchronization`,
      {
        companies,
        user,
        startDate,
        endDate,
        csv: outputType == OutputType.Csv,
        sortBy,
        onlyUpdater,
      },
      true,
      getOutputType(outputType),
    )

  const getDemoReport = (
    startDate,
    endDate,
    outputType: OutputType,
    sortBy: number,
  ) =>
    downloadFile(
      `config-api/printout/demo`,
      {
        startDate,
        endDate,
        csv: outputType == OutputType.Csv,
        sortBy,
      },
      true,
      getOutputType(outputType),
    )

  const getRealIncomeReport = (companies, period) =>
    downloadFile(
      `config-api/printout/realIncome`,
      {
        companies,
        period,
      },
      true,
      getOutputType(OutputType.Csv),
    )

  const getCompanyDataReport = (
    companies,
    groups,
    months,
    year,
    outputType: OutputType,
    sortBy: number,
    onlyPro: boolean,
    onlyPackage: boolean,
    mergeSameCompanies: boolean,
    source: number,
    updatePaymentDate: boolean,
  ) =>
    downloadFile(
      `config-api/printout/companyData`,
      {
        companies,
        groups,
        months,
        year,
        csv: outputType == OutputType.Csv,
        sortBy,
        onlyPro,
        onlyPackage,
        mergeSameCompanies,
        source,
        updatePaymentDate,
      },
      true,
      getOutputType(outputType),
    )

  const getTimesheetReport = (companies: Array<Pair<string, string>>, period) =>
    downloadFile(
      `config-api/printout/timesheet`,
      {
        companies: companies.map((c) => `${c.key}|${c.value}`),
        period,
        csv: true,
      },
      true,
      getOutputType(OutputType.Csv),
    )

  const getTimesheetDetailedReport = (
    companies: Array<Pair<string, string>>,
    period,
    detailed: boolean,
  ) =>
    downloadFile(
      `config-api/printout/timesheetDetailed`,
      {
        companies: companies.map((c) => `${c.key}|${c.value}`),
        period,
        detailed,
      },
      true,
      getOutputType(OutputType.Pdf),
    )
  const addPrintOutFav = async ({
    companyId,
    reportId,
  }: {
    companyId: string
    reportId: number
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.post(`/api/${companyId}/printout/favorites`, {
        reportType: reportId,
      })
    } catch (error) {
      console.error('Error adding printout favorite:', error)
    } finally {
      stopLoading()
    }
  }

  const deletePrintOutFav = async ({
    companyId,
    reportId,
  }: {
    companyId: string
    reportId: number
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.delete(
        `/api/${companyId}/printout/favorites/${reportId}`,
      )
    } catch (error) {
      console.error('Error deleting printout favorite:', error)
    } finally {
      stopLoading()
    }
  }
  const getFavPrintOuts = async ({ companyId }: { companyId: string }) => {
    const stopLoading = startLoading()
    try {
      const response = await axiosInstance.get(
        `/api/${companyId}/printout/favorites`,
      )
      return response.data
    } catch (error) {
      console.error('Error fetching printout favorites:', error)
      return []
    } finally {
      stopLoading()
    }
  }

  /**
   * Formaty obsługiwane przez poszczególne raporty — liczone po stronie serwera z flag
   * modelu dokumentu (HasPdfContent / HasCsvContent / HasXlsxContent), tak jak robi to WPF.
   */
  const getReportCapabilities = async ({
    companyId,
  }: {
    companyId: string
  }): Promise<ReportCapabilityDto[]> => {
    const response = await axiosInstance.get(
      `/api/${companyId}/printout/capabilities`,
    )
    return Array.isArray(response.data) ? response.data : []
  }

  /** Filtr dat delegacji zapamiętany dla raportu; null, gdy raport go nie pamięta. */
  const getReportTripFilter = async ({
    companyId,
    reportType,
  }: {
    companyId: string
    reportType: number
  }): Promise<number | null> => {
    const response = await axiosInstance.get(
      `/api/${companyId}/printout/tripFilter/${reportType}`,
    )
    return response.data?.tripFilter ?? null
  }

  const addPrintOutShipping = async ({
    companyId,
    reportId,
  }: {
    companyId: string
    reportId: number
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.post(`/api/${companyId}/printout/shipping`, {
        reportType: reportId,
      })
    } catch (error) {
      console.error('Error adding printout shipping:', error)
    } finally {
      stopLoading()
    }
  }

  const deletePrintOutShipping = async ({
    companyId,
    reportId,
  }: {
    companyId: string
    reportId: number
  }) => {
    const stopLoading = startLoading()
    try {
      await axiosInstance.delete(
        `/api/${companyId}/printout/shipping/${reportId}`,
      )
    } catch (error) {
      console.error('Error deleting printout shipping:', error)
    } finally {
      stopLoading()
    }
  }

  const getShippingPrintOuts = async ({
    companyId,
  }: {
    companyId: string
  }) => {
    const stopLoading = startLoading()
    try {
      const response = await axiosInstance.get(
        `/api/${companyId}/printout/shipping`,
      )
      return response.data
    } catch (error) {
      console.error('Error fetching printout shipping:', error)
      return []
    } finally {
      stopLoading()
    }
  }

  return {
    getPdf,
    getOutputFile,
    getActiveDriversReport,
    getSynchronizationReport,
    getAuditReport,
    getPdfFromDates,
    getCompanyDataReport,
    getTimesheetReport,
    getTimesheetDetailedReport,
    getDemoReport,
    getRealIncomeReport,
    deletePrintOutFav,
    addPrintOutFav,
    getFavPrintOuts,
    addPrintOutShipping,
    deletePrintOutShipping,
    getShippingPrintOuts,
    getReportCapabilities,
    getReportTripFilter,
  }
}

export default usePrintOutService
